import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { useTheme } from "../../../contexts/ThemeContext";

const formatDate = (timestamp) =>
  timestamp ? new Date(timestamp * 1000).toLocaleDateString() : "Sin fecha de termino";

const clampProgress = (value) => Math.max(0, Math.min(100, Number(value) || 0));

export const ProgressRowItem = ({
  course,
  grade,
  progress,
  lang,
  includeEncuesta,
  rowIndex = 0,
  onRowClick,
}) => {
  const theme = useTheme();
  const chartRef = useRef(null);

  const courseName = course.displayname || course.fullname || "";
  const start = formatDate(course.startdate);
  const end = formatDate(course.enddate);
  const normalizedProgress = progress === null || progress === undefined ? null : clampProgress(progress);
  const isEven = rowIndex % 2 === 1;

  useEffect(() => {
    if (!chartRef.current || normalizedProgress === null) return;

    const size = 36;
    const strokeWidth = 6;
    const radius = (size - strokeWidth) / 2;
    const svg = d3.select(chartRef.current);
    const arcFactory = d3.arc();
    if (!arcFactory?.innerRadius || !arcFactory?.outerRadius) return;
    const arc = arcFactory.innerRadius(radius - strokeWidth).outerRadius(radius);
    const pie = d3.pie().value((d) => d.value).sort(null);
    const chartData = [
      { label: "completed", value: normalizedProgress },
      { label: "pending", value: 100 - normalizedProgress },
    ];
    const colors = d3.scaleOrdinal([
      theme.mode === "dark"
        ? theme.colors.backgroundCompletadoLight
        : theme.colors.backgroundCompletadoDark,
      theme.mode === "dark"
        ? theme.colors.backgroundPendienteLight
        : theme.colors.backgroundPendienteDark,
    ]);

    svg.selectAll("*").remove();
    svg
      .attr("width", size)
      .attr("height", size)
      .append("g")
      .attr("transform", `translate(${size / 2},${size / 2})`)
      .selectAll("path")
      .data(pie(chartData))
      .join("path")
      .attr("d", arc)
      .attr("fill", (_, i) => colors(i));
  }, [normalizedProgress, theme]);

  const rowBackground = isEven
    ? theme.mode === "dark"
      ? "rgba(255,255,255,0.05)"
      : "#F5F5F5"
    : theme.mode === "dark"
      ? "rgba(255,255,255,0.02)"
      : "#FFFFFF";
  const titleColor = theme.mode === "dark" ? "#E0E0E0" : "#1A1A1A";
  const textColor = theme.mode === "dark" ? "#B0B0B0" : "#3D3D3D";

  return (
    <tr
      onClick={() => onRowClick?.(course)}
      className="cursor-pointer transition-colors duration-150"
      style={{ backgroundColor: rowBackground }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "#E8E8E8";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = rowBackground;
      }}
    >
      <th scope="row" className="p-[16px] font-lato font-medium text-[14px] border-none" style={{ color: titleColor }}>
        <p className="font-lato font-medium text-[14px] break-words whitespace-normal leading-snug" style={{ color: titleColor }}>
          {courseName}
        </p>
      </th>
      <td className="p-[16px] font-lato text-[14px] border-none whitespace-nowrap" style={{ color: textColor }}>
        {normalizedProgress !== null ? (
          <div className="flex items-center gap-2">
            <svg ref={chartRef} aria-hidden="true" />
            <span>{Math.floor(normalizedProgress)}%</span>
          </div>
        ) : (
          "-"
        )}
      </td>
      <td
        className="p-[16px] font-lato text-[14px] border-none whitespace-nowrap"
        style={{ color: textColor, textAlign: "center" }}
      >
        <span className="inline-block w-full text-center font-semibold">{grade}</span>
      </td>
      <td className="p-[16px] font-lato text-[14px] border-none whitespace-nowrap" style={{ color: textColor }}>
        {start}
      </td>
      <td className="p-[16px] font-lato text-[14px] border-none whitespace-nowrap" style={{ color: textColor }}>
        {end === "Sin fecha de termino" ? lang.History.fourth_column_answer : end}
      </td>
      {includeEncuesta && (
        <td className="p-[16px] font-lato text-[14px] border-none whitespace-nowrap" style={{ color: textColor }}>
          -
        </td>
      )}
    </tr>
  );
};
